<template>
  <div class="no-pages pt-12">
    <DeletedProjectBanner
      v-if="socketDeletedProject"
      :resource-type="'project'"
      :deleted-project-name="currentProject.title"
    />
    <vue-scroll
      :ops="{
        scrollPanel: { scrollingX: false },
        rail: { gutterOfSide: '6px', gutterOfEnds: '8px' },
        bar: { size: '4px' },
      }"
      class="empty-space-srcoll-container"
    >
      <div class="empty-space-content">
        <div class="no-pages-content">
          <p
            v-if="noBoards && noPages && noSprints"
            class="no-pages-title"
          >
            {{ translate('thisSpaceIsEmpty') }}
          </p>
          <p
            v-else
            class="no-pages-title"
          >
            {{ translate('views') }}
          </p>
          <p class="sub-text upper">
            {{ translate(noViews ? 'useFiltersToCreate' : 'createSomethingToGetStarted') }}
          </p>
        </div>
        <div class="plates">
          <StListItem
            v-for="resource in resources"
            :key="resource.id"
            :item-id="resource.id"
            :clickable="false"
            :icon="resource.icon"
            :title="translate(resource.title)"
            :text="translate(resource.description)"
            :button-text="translate(resource.buttonText)"
            @buttonClick="handleClick"
          />
        </div>
        <div
          v-if="!noViews"
          class="footer"
        >
          <StListItem
            :item-id="'import'"
            :clickable="false"
            :icon="'download-cloud'"
            :button-text="translate('importTasks')"
            @buttonClick="handleClick"
          >
            <div class="st-list-item-text">
              {{ translate('importYourExistingTasksFrom') }}
              <div
                v-for="(importer, index) in importerTemplates"
                :key="index"
                class="inline"
              >
                <span v-if="index !== 0">{{ displayDelimiter(index) }}</span>
                <strong>{{ $capitalizeFirstLetter(importer.id) }}</strong>
              </div>
            </div>
          </StListItem>
        </div>
      </div>
    </vue-scroll>
  </div>
</template>

<script>
import DeletedProjectBanner from '@/components/elements/DeletedProjectBanner.vue';
import addDraftPageAndUpdateSidebar from '@/mixins/addDraftPageAndUpdateSidebar';
import importMixin from '@/mixins/importMixin';
import StListItem from '@/components/elements/StListItem.vue';
import { ResourceType } from '@/types/resources';

export default {
  name: 'NoPagesBoards',
  components: {
    DeletedProjectBanner,
    StListItem,
  },
  mixins: [addDraftPageAndUpdateSidebar, importMixin],
  props: {
    noPages: {
      type: Boolean,
      required: true,
    },
    noBoards: {
      type: Boolean,
      required: true,
    },
    noSprints: {
      type: Boolean,
      required: true,
    },
    noViews: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    currentProject() {
      return this.$store.getters.getCurrentProject;
    },
    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },
    socketDeletedProject() {
      return this.$store.getters.socketDeletedProjects.includes(this.currentProject.id);
    },
    theme() {
      return this.$store.getters.getTheme;
    },
    resources() {
      if (this.noViews) {
        return [
          {
            id: ResourceType.View,
            title: this.translate('view'),
            description: this.translate('filterYourWorkspaceToCreateViews'),
            icon: 'view',
            buttonText: this.translate('createNewView'),
          },
        ];
      }
      return [
        {
          id: ResourceType.Board,
          title: this.translate('board'),
          description: this.translate('visualiseYourWorkflow'),
          icon: 'board',
          buttonText: this.translate('createBoard'),
        },
        {
          id: ResourceType.Page,
          title: this.translate('page'),
          description: this.translate('captureImportantInformation'),
          icon: 'page',
          buttonText: this.translate('createPage'),
        },
        {
          id: ResourceType.Sprints,
          title: this.translate('sprints'),
          description: this.translate('organiseWorkIntoSprints'),
          icon: 'sprint-completed',
          buttonText: this.translate('turnOnSprints'),
        },
      ];
    },
  },
  methods: {
    displayDelimiter(index) {
      if (index === 0) return '';
      return index === this.importerTemplates.length - 1 ? ` ${this.translate('orLc')} ` : ', ';
    },
    createNewBoard() {
      this.$store.dispatch('setCurrentBoardId', '');
      this.$router.push('newboard');
    },

    goToImportSettings() {
      this.$router.push(`/${this.teamSlug}/settings/import`);
    },

    goToSprintSettings() {
      this.$router.push(
        `/${this.teamSlug}/settings/${this.$cons.projectSlug}-${this.currentProject.id}/sprints`
      );
    },

    addNewView() {
      this.$router.push('newview');
    },
    handleClick(id) {
      switch (id) {
        case ResourceType.Board:
          this.createNewBoard();
          break;
        case ResourceType.Page:
          this.addDraftPageAndUpdateSidebar(this.currentProject);
          break;
        case ResourceType.Sprints:
          this.goToSprintSettings();
          break;
        case ResourceType.View:
          this.addNewView();
          break;
        case 'import':
          this.goToImportSettings();
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style lang="scss">
.no-pages {
  height: calc(100vh - 48px);
  @apply bg-background-strongest relative select-none box-border flex justify-center overflow-y-auto overflow-x-hidden;

  .deleted-bar {
    position: absolute;
    top: 0;
    width: 100%;
  }

  .empty-space-srcoll-container {
    @apply flex flex-col overflow-hidden justify-center;
    max-width: calc($spaceHomePageEmptyStateWidth + (2 * $spaceHomePageEmptyStatePadding));
    height: fit-content !important;

    .empty-space-content {
      @apply flex flex-col items-center justify-center w-full box-border gap-6;
      padding: 0 $spaceHomePageEmptyStatePadding $spaceHomePageEmptyStatePadding;

      .footer {
        border-top: 1px solid;
        @apply pt-6 border-dividers-default w-full;
      }
    }
  }

  .no-pages-content {
    .no-pages-title {
      font-weight: 500;
      font-size: 24px;
      line-height: 32px;
      text-align: center;
      @apply text-text-default;
    }
    .sub-text {
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      text-align: center;
      @apply text-text-default;
    }
    .upper {
      margin-top: 8px;
    }
    .no-pages-new-button {
      display: flex;
      flex-direction: row;
      font-size: 18px;
      line-height: 21px;
      margin-top: 16px;
      align-items: center;
      .text {
        margin-left: 45px;
        padding-right: 13px;
        text-align: center;
      }
    }
  }
  .plates {
    @apply flex flex-col w-full gap-[10px];
  }

  .line-separator {
    width: 100%;
    max-width: 680px;
    height: 1px;
    @apply bg-dividers-default;
    margin-top: 8px;
  }

  .import-button {
    margin-top: 32px;
    margin-bottom: 32px;
    margin-left: auto;
    margin-right: auto;
    height: 36px;
    @apply bg-important-bg hover:bg-important-hover;

    .button-label {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      @apply text-text-default;
    }
    em {
      background: var(--iconDefault) !important;
    }

    &.button-wrap {
      &.border {
        border: 1px solid;
        @apply border-important-border;
      }
    }
  }
}
</style>
